import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Grid,
  FormControl,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PubNub from 'pubnub';
import constants from '../../../utils/constants';
import { showSuccessToast, showErrorToast } from '../../../common/components/Helper/Utils';
import { connectEbay, getEbayStatus, disconnectEbay } from './actions/administration';
import { SpinnerRoundFilled } from "spinners-react";
import CircularProgress from "@material-ui/core/CircularProgress";

const CHANNEL = "ebay-notify";

const EbaySettings = () => {
  const [loader, setLoader] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [username, setUsername] = useState(null);
  const [storeName, setStoreName] = useState(null);
  const [email, setEmail] = useState(null);
  const [siteUrl, setSiteUrl] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const popupRef = useRef(null);
  const closeTimerRef = useRef(null);

  const pubNub = useMemo(() => {
    return new PubNub({
      publishKey: constants.PUBNUB_PUBLISHKEY,
      subscribeKey: constants.PUBNUB_SUBSCRIBEKEY,
      uuid: constants.PUBNUB_UUID,
      autoNetworkDetection: true,
      restore: true,
    });
  }, []);

  const fetchStatus = () => {
    setLoader(true);
    getEbayStatus((err, res) => {
      if (res?.status === 1 && res.data) {
        setIsConnected(!!res.data.isConnected);
        setUsername(res.data.username || null);
        setStoreName(res.data.storeName || null);
        setEmail(res.data.email || null);
        setSiteUrl(res.data.siteUrl || null);
      }
      setLoader(false);
    });
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    const listener = {
      message: (event) => {
        if (event?.channel !== CHANNEL) return;
        const type = event?.message?.type;
        if (type === 'EBAY_SUCCESS' || type === 'EBAY_FAILED') {
          setIsConnecting(false);
          if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
          }
          if (type === 'EBAY_SUCCESS') {
            showSuccessToast('eBay account connected successfully');
          } else {
            showErrorToast('Failed to connect eBay account');
          }
          fetchStatus();
        }
      },
    };
    pubNub.addListener(listener);
    pubNub.subscribe({ channels: [CHANNEL] });

    const handlePopupMessage = (event) => {
      if (event?.data?.type === 'EBAY_VERIFIED') {
        setIsConnecting(false);
        fetchStatus();
      }
    };
    window.addEventListener('message', handlePopupMessage);

    return () => {
      pubNub.removeListener(listener);
      pubNub.unsubscribe({ channels: [CHANNEL] });
      window.removeEventListener('message', handlePopupMessage);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [pubNub]);

  const openPopup = (url) => {
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    popupRef.current = window.open(
      url,
      'ebayAppSetup',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    closeTimerRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        clearInterval(closeTimerRef.current);
        setIsConnecting(false);
        fetchStatus();
      }
    }, 1000);
  };

  const handleConnect = () => {
    setIsConnecting(true);
    connectEbay((err, res) => {
      if (res?.status === 1 && res.data?.authUrl) {
        openPopup(res.data.authUrl);
      } else {
        setIsConnecting(false);
        showErrorToast(res?.message || 'Failed to start eBay connection');
      }
    });
  };

  const handleDisconnect = () => {
    setIsDisconnecting(true);
    disconnectEbay((err, res) => {
      setIsDisconnecting(false);
      if (res?.status === 1) {
        showSuccessToast('eBay account disconnected');
        fetchStatus();
      } else {
        showErrorToast(res?.message || 'Failed to disconnect eBay account');
      }
    });
  };

  if (loader) {
    return (
      <div className="d-spinner">
        <SpinnerRoundFilled
          size={51}
          thickness={125}
          speed={80}
          color="rgba(57, 107, 172, 1)"
          className="spp"
          enabled={loader}
        />
      </div>
    );
  }

  const isVerified = isConnected && (storeName || email || username);

  return (
    <Paper
      className="space-3"
      style={{
        height: "auto",
        borderTop: "1px solid rgba(0, 0, 0, 0.18)",
      }}
    >
      <div
        className=" space-left-20"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="h6"
            component="p"
            className="primary-text uppercase"
          >
            eBay INFORMATION
          </Typography>
          {!isVerified && (
            <Chip
              label="Not Verified"
              size="small"
              className="void-status"
              style={{ marginLeft: "8px" }}
            />
          )}
          {isVerified && (
            <>
              <VerifiedIcon
                style={{ color: "green", marginLeft: "8px" }}
              />
              <Chip
                label="Verified"
                size="small"
                style={{ color: "green", backgroundColor: "white" }}
              />
            </>
          )}
        </div>
      </div>
      <div className="space-2">
        {isVerified && (
          <>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label className="myprofile-font">eBay Store Name:</label>
                  <TextField
                    variant="standard"
                    disabled
                    fullWidth
                    value={storeName || username || ''}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label className="myprofile-font">eBay Email:</label>
                  <TextField variant="standard" disabled fullWidth value={email || ''} />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label className="myprofile-font">eBay URL:</label>
                  <TextField
                    variant="standard"
                    disabled
                    fullWidth
                    value={siteUrl || ''}
                    InputProps={{
                      endAdornment: siteUrl && (
                        <InputAdornment position="end">
                          <Tooltip title="Copy URL">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(siteUrl);
                                showSuccessToast('Copied to clipboard');
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Open URL">
                            <IconButton
                              size="small"
                              onClick={() => window.open(siteUrl, '_blank')}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Button
              variant="outlined"
              color="error"
              disabled={isDisconnecting}
              style={{ marginTop: "20px", display: 'none' }}
              onClick={handleDisconnect}
              endIcon={
                isDisconnecting ? (
                  <CircularProgress style={{ width: 20, height: 20 }} />
                ) : null
              }
            >
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect eBay'}
            </Button>
          </>
        )}
        {!isVerified && (
          <Button
            className="primary-bg color-white"
            variant="contained"
            color="primary"
            disabled={isConnecting}
            onClick={handleConnect}
            endIcon={
              isConnecting ? (
                <CircularProgress style={{ width: 20, height: 20, color: '#fff' }} />
              ) : null
            }
          >
            {isConnecting ? 'Connecting...' : 'Connect to eBay'}
          </Button>
        )}
      </div>
    </Paper>
  );
};

export default EbaySettings;
